import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListInStockSectionStyles {
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    input?: TextFieldPresentationProps;
}

export const productListInStockSectionStyles: ProductListInStockSectionStyles = {
    input: {
        iconProps: { src: Search },
        cssOverrides: {
            formField: css`
                width: 100%;
                div {
                    input {
                        border-radius: 40px !important;
                        padding: 4px 16px !important;
                        height: 32px;
                        background-color: ${COLORS.border.darkShade} !important;
                        color: ${COLORS.black} !important;
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                    }
                    span {
                        color: ${getProp("theme.typography.p.color")} !important;
                        top: 0px !important;
                    }
                }
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            display: grid !important;
            padding: 15px;
            border-bottom: 1px solid rgb(224, 223, 224) !important;
            &:not(:last-child) {
                padding-bottom: 5px;
                padding-top: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: row;
            span {
                border: 2px solid #9e9d9e !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                &:focus {
                    box-shadow: none !important;
                }
            }
            label {
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 20px;
                color: ${COLORS.black};
                width: 100%;
                margin: 0 !important;
                padding-left: 10px;
                div {
                    font-size: ${TYPOGRAPHY.size.base} !important;
                }
            }
            span[role="checkbox"] {
                height: 18px;
                width: 18px;
                border-radius: 2px !important;
                min-width: 18px !important;
                color: ${COLORS.black} !important;
                svg {
                    height: 18px;
                    width: 18px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: row;
            span {
                border: 2px solid ${COLORS.black} !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                &:focus {
                    box-shadow: none !important;
                }
            }
            label {
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                color: ${COLORS.black};
                width: 100%;
                margin: 0 !important;
                padding-left: 10px;
                div {
                    font-size: ${TYPOGRAPHY.size.base} !important;
                }
            }
            span[role="checkbox"] {
                height: 18px;
                width: 18px;
                min-width: auto !important;
                border-radius: 2px !important;
                background: none !important;
                color: ${COLORS.black} !important;
                svg {
                    height: 18px;
                    width: 18px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListInStockSectionStyles;
    expandByDefault: boolean;
}

const ProductListInStockSection: FC<Props> = ({ facets, onChangeFacet, showMoreLimit, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(productListInStockSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const [filter, setFilter] = useState("");

    return (
        <>
            {limitedFacets
                ?.filter(facet => facet.name?.toLowerCase().includes(filter.toLowerCase()) || filter === "")
                .map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            <StyledWrapper
                                css={css`
                                    display: flex;
                                    flex-direction: row;
                                `}
                            >
                                <StyledWrapper
                                    css={css`
                                        flex-basis: 90%;
                                        font-weight: 600;
                                        font-size: 16px;
                                        line-height: 18px;
                                        ${({ theme }: { theme: BaseTheme }) =>
                                            breakpointMediaQueries(theme, [
                                                css`
                                                    font-size: 14px;
                                                `,
                                                css`
                                                    font-size: 14px;
                                                `,
                                                css`
                                                    font-size: 14px;
                                                `,
                                                null,
                                                null,
                                            ])}
                                    `}
                                >
                                    {facet.name}
                                </StyledWrapper>
                                <StyledWrapper
                                    css={css`
                                        color: ${COLORS.background.tag};
                                    `}
                                >
                                    {facet.count !== -1 && `(${facet.count})`}
                                </StyledWrapper>
                            </StyledWrapper>
                        </Checkbox>
                    </CheckboxGroup>
                ))}
            {facets &&
                facets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("See All")}
                    </Link>
                ))}
        </>
    );
};

export default ProductListInStockSection;
